import { z } from 'zod';
import { request } from '@/lib/api';
import i18n from '@/lib/i18n';

/* ---------------------------------------------------------------------------
   סוגי עבודה (job-type-templates). מפתח המטמון זהה לזה שדיאלוג "משימה חדשה"
   משתמש בו, כך שהוספה כאן מופיעה שם מיד.
   --------------------------------------------------------------------------- */

export const JOB_TYPES_KEY = ['job-type-templates'] as const;

const checklistItemSchema = z
  .object({ label: z.string(), priceCode: z.string().optional(), sku: z.string().optional(), qty: z.number().optional() })
  .passthrough();
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const jobTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  requiredSkill: z.string().nullish(),
  defaultChecklist: z.array(checklistItemSchema).catch([]),
  durationMinutes: z.number().catch(120),
  leadTimeDays: z.number().catch(0),
});
export type JobType = z.infer<typeof jobTypeSchema>;

export interface JobTypeInput {
  name: string;
  requiredSkill: string | null;
  defaultChecklist: Array<{ label: string; priceCode?: string; sku?: string; qty?: number }>;
  durationMinutes: number;
  leadTimeDays: number;
}

export const importedJobTypeSchema = z.object({
  name: z.string(),
  requiredSkill: z.string().nullable(),
  checklist: z.array(z.string()),
  durationMinutes: z.number().nullable(),
  leadTimeDays: z.number().nullable(),
});
export type ImportedJobType = z.infer<typeof importedJobTypeSchema>;

const importPreviewSchema = z.object({
  items: z.array(importedJobTypeSchema),
  source: z.enum(['table', 'ai']),
  warnings: z.array(z.string()),
});
export type ImportPreview = z.infer<typeof importPreviewSchema>;

/** רק השדות שהשרת מקבל — `done` ושאר שדות פנימיים לא נשלחים. */
const clean = (items: ChecklistItem[]) =>
  items.map(({ label, priceCode, sku, qty }) => ({
    label,
    ...(priceCode ? { priceCode } : {}),
    ...(sku ? { sku } : {}),
    ...(qty ? { qty } : {}),
  }));

export const jobTypesApi = {
  list: (signal?: AbortSignal) => request('/job-type-templates', { schema: z.array(jobTypeSchema), signal }),
  create: (v: JobTypeInput) =>
    request('/job-type-templates', {
      method: 'POST',
      body: {
        name: v.name,
        ...(v.requiredSkill ? { requiredSkill: v.requiredSkill } : {}),
        fields: [],
        defaultChecklist: clean(v.defaultChecklist),
        durationMinutes: v.durationMinutes,
        leadTimeDays: v.leadTimeDays,
      },
    }),
  update: (id: string, v: JobTypeInput) =>
    request(`/job-type-templates/${id}`, {
      method: 'PATCH',
      body: {
        name: v.name,
        requiredSkill: v.requiredSkill,
        defaultChecklist: clean(v.defaultChecklist),
        durationMinutes: v.durationMinutes,
        leadTimeDays: v.leadTimeDays,
      },
    }),
  remove: (id: string) => request(`/job-type-templates/${id}`, { method: 'DELETE' }),
  previewImport: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request('/job-type-templates/import/preview', { method: 'POST', body, schema: importPreviewSchema });
  },
  importMany: (items: ImportedJobType[]) =>
    request('/job-type-templates/import', {
      method: 'POST',
      body: {
        items: items.map((i) => ({
          name: i.name,
          ...(i.requiredSkill ? { requiredSkill: i.requiredSkill } : {}),
          checklist: i.checklist,
          ...(i.durationMinutes != null ? { durationMinutes: i.durationMinutes } : {}),
          ...(i.leadTimeDays != null ? { leadTimeDays: i.leadTimeDays } : {}),
        })),
      },
      schema: z.object({ created: z.number(), skipped: z.array(z.string()) }),
    }),
  addTradeDefaults: () =>
    request('/job-type-templates/trade-defaults', { method: 'POST', schema: z.object({ added: z.number() }) }),
};

/** "2 שעות", "45 דק׳", "שעה וחצי" — בשפת הממשק. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return i18n.t('job-types:duration.minutes', { count: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = i18n.t('job-types:duration.hours', { count: h });
  return m === 0
    ? hours
    : m === 30
      ? i18n.t('job-types:duration.hoursHalf', { hours })
      : i18n.t('job-types:duration.hoursMinutes', { hours, minutes: m });
}

export function formatLead(days: number): string {
  if (days === 0) return i18n.t('job-types:lead.none');
  if (days % 7 === 0) return i18n.t('job-types:lead.weeks', { count: days / 7 });
  return i18n.t('job-types:lead.days', { count: days });
}
